/**
 * Global axios resilience for the Emergent embedded preview iframe.
 *
 * Problems addressed:
 *  - React.StrictMode double-mounts useEffect in dev → identical GETs fire twice,
 *    sometimes producing 2 toasts when the proxy hiccups.
 *  - Iframe preview can drop the first request right after mount (Cloudflare edge /
 *    storage partitioning timing) → spurious "Eroare la încărcarea ..." toast, even
 *    though a manual refresh works fine.
 *
 * Mitigations (transparent to page components):
 *  1. Dedupe concurrent identical GETs — second caller awaits the first promise.
 *  2. Retry once on pure-network errors (no HTTP response) with 600ms backoff.
 *     Does NOT retry HTTP 4xx/5xx — those are real server errors.
 *  3. Mark canceled / retried-network errors with `error.suppressToast = true` so
 *     pages can skip the toast if desired.
 */
import axios from "axios";

const inflightGets = new Map(); // key -> Promise

function keyFor(config) {
  const params = config.params ? JSON.stringify(config.params) : "";
  const auth =
    (config.headers && (config.headers.Authorization || config.headers.authorization)) || "";
  return `GET:${config.url}:${params}:${auth.slice(-16)}`;
}

let installed = false;

export function installAxiosResilience() {
  if (installed) return;
  installed = true;

  const originalRequest = axios.request.bind(axios);

  axios.request = function patchedRequest(config = {}) {
    const method = (config.method || "get").toLowerCase();

    // ---- Dedupe in-flight GETs ----
    if (method === "get" && !config.__skipDedupe) {
      const k = keyFor(config);
      if (inflightGets.has(k)) {
        return inflightGets.get(k);
      }
      const p = doRequestWithRetry(originalRequest, config);
      inflightGets.set(k, p);
      p.finally(() => inflightGets.delete(k));
      return p;
    }

    return doRequestWithRetry(originalRequest, config);
  };
}

async function doRequestWithRetry(originalRequest, config) {
  try {
    return await originalRequest(config);
  } catch (error) {
    const isNetwork = !error.response; // no HTTP response → network/timeout/aborted
    const method = (config.method || "get").toLowerCase();

    if (isNetwork && method === "get" && !config.__retried) {
      await new Promise((r) => setTimeout(r, 600));
      try {
        return await originalRequest({ ...config, __retried: true, __skipDedupe: true });
      } catch (retryErr) {
        retryErr.suppressToast = true;
        throw retryErr;
      }
    }

    if (axios.isCancel && axios.isCancel(error)) {
      error.suppressToast = true;
    }
    throw error;
  }
}
